import * as Accordion from "#root/components/accordion";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import { WithSidebar } from "#root/components/withSidebar";
import { startDownload } from "#root/utility/download";
import { useData } from "#root/utility/hooks";
import { monthNames } from "#root/utility/months";
import formatNumber from "cloudquote-number-format";
import type { ReactNode } from "react";
import { Fragment, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { getNewsRPMInstanceByCode } from "#root/schema/platform/newsrpm-instance";
import PageLayout from "./layout";

export default function UsagePage({ instanceId, ymd }): ReactNode {
  const months = useMemo(() => {
    const months: { name: string; ymd: string }[] = [];
    const date = new Date();
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    const start = new Date("2020-05-01");

    for (let i = 0; i < 12 && date > start; i++) {
      const ymd = date.toISOString().replace(/T.*/, "");
      months.push({
        name: monthNames[date.getUTCMonth()] + " " + date.getUTCFullYear(),
        ymd,
      });
      date.setUTCMonth(date.getUTCMonth() - 1);
    }

    return months;
  }, []);

  return (
    <PageLayout title="Usage">
      <WithSidebar>
        <UsageSidebar {...{ months, ymd, instanceId }} />
        <section>
          {ymd ? (
            <UsageTable {...{ ymd, instanceId }} />
          ) : (
            <Fragment>
              <h2>Content Usage</h2>
              <p>Please select a month to view Content Usage</p>
            </Fragment>
          )}
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function UsageSidebar({ ymd, instanceId, months }) {
  return (
    <Sidebar>
      <Accordion.Parent initialItemId={ymd} initialGroupId="By Month">
        <Accordion.Group label="By Month">
          {months.map(({ ymd, name }) => (
            <Accordion.Item key={ymd} itemId={ymd}>
              <Link to={`/admin/platform/newsrpm-instance/${instanceId}/usage/${ymd}`}>{name}</Link>
            </Accordion.Item>
          ))}
        </Accordion.Group>
      </Accordion.Parent>
    </Sidebar>
  );
}

const UsageSchema = z.object({
  rows: z.array(
    z.object({
      providerCode: z.string(),
      provider: z.string(),
      count: z.number(),
    }),
  ),
});

function UsageTable({ instanceId, ymd }) {
  const startDate = useMemo(() => new Date(ymd), [ymd]);
  const endDate = useMemo(() => {
    const d = new Date(ymd);
    d.setUTCMonth(d.getUTCMonth() + 1);
    d.setUTCMilliseconds(d.getUTCMilliseconds() - 1);
    return d;
  }, [ymd]);

  const [details] = useData(async () => {
    let response = await getNewsRPMInstanceByCode(instanceId).fetch("report/providerStats", {
      query: {
        minCreationTime: startDate.toISOString(),
        maxCreationTime: endDate.toISOString(),
      },
    });

    response = await response.json();

    return UsageSchema.parse(response).rows;
  }, [instanceId, ymd]);

  const handleDownloadClick = useCallback(() => {
    const csv = [["Provider Code", "Name", "Article Count"]];
    for (const { providerCode, provider, count } of details ?? []) {
      csv.push([providerCode, provider, String(count)]);
    }

    startDownload({
      filename: `ContentReport-${instanceId}-${startDate.getUTCFullYear()}-${monthNames[startDate.getUTCMonth()]}.csv`,
      content: new Blob([csv.map(row => row.join(",")).join("\n")], { type: "text/csv" }),
    });
  }, [startDate, instanceId, details]);

  if (!details) {
    return <GlyphLoading/>;
  }

  return (
    <div className="usage-table">
      <h2>
        {monthNames[startDate.getUTCMonth()]} {startDate.getUTCFullYear()} - Content Usage{" "}
        <button type="button" className="flat" onClick={handleDownloadClick}>
          Download
        </button>
      </h2>
      <table className="table table-striped">
        <thead>
        <tr>
          <th>Provider Code</th>
          <th>Name</th>
          <th>Article Count</th>
        </tr>
        </thead>
        <tbody>
        {details?.map(({ provider, providerCode, count }) => (
          <tr key={providerCode}>
            <td>{providerCode}</td>
            <td>{provider}</td>
            <td>{formatNumber(count, { Comma: true })}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}
